import { Form } from '@arco-design/web-react';
import { t } from '@bay-email/core';
import BayCheckbox from '@bay-ui/BayCheckbox';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useField, UseFieldConfig } from 'react-final-form';
import { Input } from './Input';
import styles from './index.module.scss';

interface AutoCheckboxInputProps {
	name: string;
	label: React.ReactNode;
	config?: UseFieldConfig<any>;
	validate?: (value: any) => string | undefined | Promise<string | undefined>;
	autoValue?: string;
	autoLabel?: string;
	placeholder?: string;
	/**
	 * On Auto, show `placeholder` as grey text in an empty, still-typeable box
	 * instead of the literal word "Auto" in a disabled one. Callers pass the
	 * value the block actually renders at, so the field never reads "Auto"
	 * when a real number is knowable.
	 */
	showPlaceholderWhenAuto?: boolean;
	/**
	 * Set false to drop the Auto checkbox entirely — the field then only accepts
	 * an explicit number. Used by dynamic `cover` image fields, which crop to
	 * fill a box and so need both dimensions pinned.
	 */
	allowAuto?: boolean;
	/**
	 * Lock the field. Used where a value could be typed but not honoured — a
	 * static image whose aspect ratio is unknowable can't keep width and height
	 * in step, and a half-set pair renders differently from what the panel says.
	 */
	disabled?: boolean;
	/**
	 * Drive the Auto checkbox from outside instead of this field's own state.
	 * Lets a caller keep two fields' checkboxes as one control — a static
	 * image's width and height are either both Auto or neither.
	 */
	autoChecked?: boolean;
	/** Fires when the user clicks the Auto checkbox. */
	onAutoToggle?: (_checked: boolean) => void;
	/**
	 * Fires after the user changes this field (typing or leaving Auto). Lets a
	 * caller keep a linked field in step — an image's width and height, say.
	 */
	onValueCommitted?: (_value: string | undefined) => void;
}

function isAutoStoredValue(value: unknown, autoValue?: string) {
	if (value === undefined || value === null || value === '') {
		return true;
	}

	if (typeof value === 'string') {
		const normalizedValue = value.trim().toLowerCase();
		if (normalizedValue === 'auto') {
			return true;
		}

		if (typeof autoValue === 'string') {
			return normalizedValue === autoValue.trim().toLowerCase();
		}
	}

	return value === autoValue;
}

export function AutoCheckboxInput({
	name,
	label,
	config,
	validate,
	autoValue,
	autoLabel = t('Auto'),
	placeholder,
	showPlaceholderWhenAuto = false,
	allowAuto = true,
	disabled = false,
	autoChecked,
	onAutoToggle,
	onValueCommitted,
}: AutoCheckboxInputProps) {
	const suppressAutoSyncRef = useRef(false);
	// Last real number typed here, so a field with no Auto state can be restored
	// when the user blurs it empty.
	const lastExplicitValueRef = useRef<string | null>(null);
	const { input, meta } = useField<string | undefined>(name, {
		subscription: {
			value: true,
			touched: true,
			error: true,
		},
		validate,
		parse: config?.parse,
		format: config?.format,
	});

	const storedAuto = isAutoStoredValue(input.value, autoValue);
	const [ownManualMode, setIsManualMode] = useState(!storedAuto);
	// A caller can own the Auto state so two fields share one checkbox.
	const isManualMode = autoChecked === undefined ? ownManualMode : !autoChecked;

	useEffect(() => {
		if (!storedAuto) {
			setIsManualMode(true);
			suppressAutoSyncRef.current = false;
			return;
		}

		if (!suppressAutoSyncRef.current) {
			setIsManualMode(false);
		}

		suppressAutoSyncRef.current = false;
	}, [name, storedAuto]);

	// Track the stored number from any source — typing, or a programmatic sync
	// from the paired field — so the restore above uses the real current value.
	useEffect(() => {
		if (!storedAuto && input.value) {
			lastExplicitValueRef.current = input.value.toString();
		}
	}, [input.value, storedAuto]);

	const displayValue = useMemo(() => {
		if (!isManualMode) {
			// With Auto disallowed there is no Auto state to name — an unset field
			// is simply empty and waiting for a number.
			return showPlaceholderWhenAuto || !allowAuto ? '' : autoLabel;
		}

		if (storedAuto) {
			return '';
		}

		return input.value?.toString() || '';
	}, [
		allowAuto,
		autoLabel,
		input.value,
		isManualMode,
		showPlaceholderWhenAuto,
		storedAuto,
	]);

	const setAutoState = useCallback(() => {
		setIsManualMode(false);
		suppressAutoSyncRef.current = false;
		input.onChange(autoValue);
	}, [autoValue, input]);

	const onToggleAuto = useCallback(
		(checked: boolean) => {
			onAutoToggle?.(checked);

			if (checked) {
				setAutoState();
				return;
			}

			suppressAutoSyncRef.current = true;
			setIsManualMode(true);
		},
		[onAutoToggle, setAutoState],
	);

	const onInputChange = useCallback(
		(nextValue: string) => {
			const numericValue = nextValue.replace(/\D+/g, '');

			// Stay in manual mode while editing. Clearing the field must NOT flip to
			// Auto on each keystroke — that decision is deferred to onBlur. Suppress
			// the auto-sync effect so emptying the value doesn't switch mode mid-edit.
			setIsManualMode(true);

			if (!numericValue) {
				suppressAutoSyncRef.current = true;
				input.onChange(undefined);
				onValueCommitted?.(undefined);
				return;
			}

			lastExplicitValueRef.current = numericValue;
			input.onChange(numericValue);
			onValueCommitted?.(numericValue);
		},
		[input, onValueCommitted],
	);

	const onInputBlur = useCallback(() => {
		if (isAutoStoredValue(input.value, autoValue)) {
			if (allowAuto) {
				// Editing is done: an empty field now means the user wants Auto.
				setAutoState();
			} else if (lastExplicitValueRef.current) {
				// This field has no Auto state, so an empty value is not a state it
				// can rest in — an unset width classifies the same as 'auto' further
				// down and would silently drop the block out of its fit rule. Put the
				// last real number back instead.
				input.onChange(lastExplicitValueRef.current);
				onValueCommitted?.(lastExplicitValueRef.current);
			}
		}
		input.onBlur();
	}, [allowAuto, autoValue, input, onValueCommitted, setAutoState]);

	// Without an Auto state the input is always live and always shows its
	// placeholder — there is no disabled "Auto" mode to fall into.
	const isEditable =
		!disabled && (isManualMode || showPlaceholderWhenAuto || !allowAuto);

	return (
		<Form.Item
			style={{
				marginTop: '0px',
				marginLeft: '0px',
				marginRight: '0px',
				marginBottom: '12px',
			}}
			label={
				<span style={{ fontWeight: 600, color: '#000000', textTransform: 'none' }}>
					{label}
				</span>
			}
			labelAlign='left'
			labelCol={{
				span: 24,
				style: {
					paddingRight: 0,
					paddingLeft: 0,
				},
			}}
			wrapperCol={{
				span: 24,
				style: {
					paddingLeft: 0,
				},
			}}
			validateStatus={meta.touched && meta.error ? 'error' : undefined}
			help={meta.touched && meta.error ? meta.error : undefined}
		>
			<div className={styles.autoCheckboxControl}>
				<Input
					autoComplete='off'
					inputMode='numeric'
					pattern='[0-9]*'
					quickchange={isEditable}
					value={displayValue}
					disabled={!isEditable}
					onChange={onInputChange}
					onBlur={onInputBlur}
					placeholder={isEditable ? placeholder : undefined}
					aria-label={typeof label === 'string' ? label : t('Value')}
				/>

				{allowAuto && (
					<div className={styles.autoCheckboxLabel}>
						<BayCheckbox
							id={`${name}-auto`}
							name={`${name}-auto`}
							isChecked={!isManualMode}
							disabled={disabled}
							label={autoLabel}
							onChangeFunc={event => {
								onToggleAuto(event.target.checked);
								input.onBlur();
							}}
						/>
					</div>
				)}
			</div>
		</Form.Item>
	);
}
